import {
  ApplicationInsights,
  DistributedTracingModes,
  type ITelemetryItem,
  SeverityLevel,
} from '@microsoft/applicationinsights-web'
import { getRuntimeConfig } from '@/runtime-config'

const AI_COOKIE_NAME = 'ai_cs'
const PACKAGE_NAME = '@ironsight/frontend'
const CLOUD_ROLE = 'IronSight.Web.ClientApp'

const VueApplicationInsights = {
  install(Vue, { router }) {
    // Cookie injection works when ASP.NET Core serves the frontend directly.
    // With Static Web App hosting, fall back to runtime config.
    const connectionString =
      getCookie(AI_COOKIE_NAME) || getRuntimeConfig().applicationInsightsConnectionString

    if (!connectionString) {
      console.debug(
        '[Vue Application Insights] No connection string provided. Application Insights will not be initialized.',
      )
      return
    }

    if (!router) {
      console.debug(
        '[Vue Application Insights] No router provided. Application Insights will not track page views.',
      )
      return
    }

    Vue.appInsights = createAppInsights(connectionString)
    addCloudRoleInitializer(Vue.appInsights)
    addProductTelemetrySamplingInitializer(Vue.appInsights)
    Vue.appInsights.loadAppInsights()

    setupPageTracking(router, Vue)
    setupErrorHandling(Vue)

    Object.defineProperty(Vue.prototype, '$appInsights', {
      get: () => Vue.appInsights,
    })
  },
}

function createAppInsights(connectionString: string) {
  return new ApplicationInsights({
    config: {
      connectionString,
      // Exclude noisy dependencies from tracking
      correlationHeaderExcludedDomains: [
        'r.lr-in.com',
        'lr-in.com',
        'firestore.googleapis.com',
        'firebaseio.com',
        'googleapis.com',
      ],
      // Increase max AJAX calls per page to prevent "ajax monitoring paused" warnings
      // Default is 500, increase to 2000 for SPA with many API calls
      maxAjaxCallsPerView: 2000,
      // Disable automatic dependency tracking for excluded domains
      disableFetchTracking: false,
      disableAjaxTracking: false,
      distributedTracingMode: DistributedTracingModes.W3C,
      // Add exclusion patterns for AJAX tracking
      excludeRequestFromAutoTrackingPatterns: [
        /r\.lr-in\.com/i,
        /firestore\.googleapis\.com/i,
        /firebaseio\.com/i,
      ],
      // Sampling is handled per-type by addProductTelemetrySamplingInitializer:
      // custom events and page views are always sent; everything else is sampled at 5%.
      samplingPercentage: 100,
      // Excluding `unload` keeps the bfcache-hostile listener unregistered, but the
      // flush must stay on: the SDK registers its `pagehide` listener inside the
      // `disableFlushOnBeforeUnload` branch, and `disableFlushOnUnload` falls back to
      // this same flag — so disabling it leaves no unload handler at all and discards
      // whatever is buffered when the tab closes.
      disablePageUnloadEvents: ['unload'],
      disableFlushOnBeforeUnload: false,
    },
  })
}

// The hash key below is the operation ID, which setupTelemetryOperationTracking rotates per
// navigation, so sampling is all-or-nothing per navigation. Page views are exempt
// because a 5% sample can't show a rare path, a small customer, or one named account.
// PageviewPerformanceData is not used for flows and stays sampled.
const UNSAMPLED_BASE_TYPES = ['EventData', 'PageviewData']

function addProductTelemetrySamplingInitializer(appInsights: ApplicationInsights) {
  appInsights.addTelemetryInitializer((envelope: ITelemetryItem) => {
    if (envelope.baseType && UNSAMPLED_BASE_TYPES.includes(envelope.baseType)) {
      return
    }
    // Sample the remaining telemetry (dependencies, exceptions, page load timings) at 5%.
    // Use operation ID as the hash key so all telemetry for the same operation
    // is sampled in or out together.
    const operationId =
      (envelope.tags as Record<string, string> | undefined)?.['ai.operation.id'] ??
      (envelope.ext?.trace as { traceID?: string } | undefined)?.traceID ??
      (envelope.ext?.telemetryTrace as { traceID?: string } | undefined)?.traceID ??
      ''
    const score = operationId ? hashCode(operationId) % 100 : Math.random() * 100
    if (score >= 5) {
      return false
    }
  })
}

function hashCode(s: string): number {
  let h = 0
  for (let i = 0; i < s.length; i++) {
    h = (Math.imul(31, h) + s.charCodeAt(i)) | 0
  }
  return Math.abs(h)
}

function addCloudRoleInitializer(appInsights: ApplicationInsights) {
  appInsights.addTelemetryInitializer((item: ITelemetryItem) => {
    if (item.tags) {
      item.tags['ai.cloud.role'] = CLOUD_ROLE
    }
  })
}

const HEX_DIGITS = '0123456789abcdef'

// A W3C trace-id is 16 bytes rendered as 32 lowercase hex characters. `generateW3CId`
// is not reachable from `@microsoft/applicationinsights-web` — it is only exported from
// the transitive `applicationinsights-core-js`, and pinning that directly to reach one
// function invites version skew — so this mirrors what the SDK does: the platform CSPRNG
// where it exists, and Math.random where it doesn't, as under jsdom.
function newOperationId(): string {
  const bytes = new Uint8Array(16)

  if (globalThis.crypto?.getRandomValues) {
    globalThis.crypto.getRandomValues(bytes)
  } else {
    for (let i = 0; i < bytes.length; i++) {
      bytes[i] = (Math.random() * 256) | 0
    }
  }

  let id = ''
  for (let i = 0; i < bytes.length; i++) {
    id += HEX_DIGITS[bytes[i] >> 4] + HEX_DIGITS[bytes[i] & 0x0f]
  }
  return id
}

export function setupTelemetryOperationTracking(router, Vue) {
  router.beforeEach((route, _, next) => {
    // This guard must be registered before guards that make requests. The SDK
    // generates one trace ID per page load unless enableAutoRouteTracking is set,
    // which we don't use because it would double-track our manual page views and
    // name operations by pathname instead of route name.
    const traceCtx = Vue.appInsights?.core?.getTraceCtx()
    if (traceCtx) {
      traceCtx.setTraceId(newOperationId())
      traceCtx.setName(route.name)
    }

    next()
  })
}

function setupPageTracking(router, Vue) {
  router.beforeEach((route, _, next) => {
    const name = PACKAGE_NAME + '/' + route.name
    Vue.appInsights.startTrackPage(name)
    next()
  })

  router.afterEach((route) => {
    const name = PACKAGE_NAME + '/' + route.name
    const url = location.protocol + '//' + location.host + route.fullPath
    Vue.appInsights.stopTrackPage(name, url)
  })

  router.onError((error) => {
    Vue.appInsights.trackException({
      error,
      exception: error,
      severity: SeverityLevel.Error,
    })
  })
}

function setupErrorHandling(Vue) {
  const initialErrorHandler = Vue.config.errorHandler

  Vue.config.errorHandler = (error, instance, info) => {
    if (initialErrorHandler) {
      initialErrorHandler(error, instance, info)
    }
    Vue.appInsights.trackException({ exception: error }, { info })
  }
}

function getCookie(name: string): string | undefined {
  const value = ('; ' + document.cookie).split(`; ${name}=`).pop()?.split(';').shift()
  return value ? decodeURIComponent(value) : undefined
}

export default VueApplicationInsights
